import { useMutation, useQueryClient } from "@tanstack/react-query";
import i18next from "i18next";
import { ApiTransferFusd } from "@/api";
import { TransferFusdResultCode, type TransferFusdRequest } from "@/api/types";
import { parseChainError } from "@/lib/chainError";
import { showError, showSuccess } from "@/lib/toast";
import { getTokenName, INTEGRAL_COIN_CONTRACT } from "@/config/common";
import { CCC_AIF_ADDRESS, CCC_FUSD_ADDRESS, USDT_ADDRESS } from "@/config/env";

export function useTransferFusd() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: TransferFusdRequest) => {
      const res = await ApiTransferFusd(data);
      console.log("useTransferFusd", res.data);
      return res.data;
    },
    onSuccess: data => {
      const code = data.resultCode;
      if (code === TransferFusdResultCode.Success) {
        queryClient.invalidateQueries({ queryKey: ["balanceV2"] });
        showSuccess(
          i18next.t("toast.transferFusd.success", {
            tokenName: getTokenName(CCC_FUSD_ADDRESS),
            tokenNameUsdt: getTokenName(USDT_ADDRESS),
            tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
            tokenNameAifx: getTokenName(INTEGRAL_COIN_CONTRACT),
          })
        );
        return;
      }

      const key =
        TRANSFER_FUSD_RESULT_I18N_KEY[
          code as keyof typeof TRANSFER_FUSD_RESULT_I18N_KEY
        ] ?? "toast.transferFusd.result.unknown";

      showError(
        i18next.t(key, {
          tokenName: getTokenName(CCC_FUSD_ADDRESS),
          tokenNameUsdt: getTokenName(USDT_ADDRESS),
          tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
          tokenNameAifx: getTokenName(INTEGRAL_COIN_CONTRACT),
        })
      );
    },
    onError: (err: unknown) => {
      showError(
        i18next.t(parseChainError(err), {
          tokenName: getTokenName(CCC_FUSD_ADDRESS),
          tokenNameUsdt: getTokenName(USDT_ADDRESS),
          tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
          tokenNameAifx: getTokenName(INTEGRAL_COIN_CONTRACT),
        })
      );
    },
  });
}

/** 与 {@link TransferFusdResultCode} 对应的 i18n：`toast.transferFusd.result.*` */
export const TRANSFER_FUSD_RESULT_I18N_KEY: Record<
  Exclude<TransferFusdResultCode, TransferFusdResultCode.Success>,
  string
> = {
  [TransferFusdResultCode.Insufficient]:
    "toast.transferFusd.result.insufficient",
  [TransferFusdResultCode.Close]: "toast.transferFusd.result.close",
  [TransferFusdResultCode.AmountErr]: "toast.transferFusd.result.amountErr",
  [TransferFusdResultCode.ExpireSignErr]:
    "toast.transferFusd.result.expireSignErr",
  [TransferFusdResultCode.ContractErr]: "toast.transferFusd.result.contractErr",
  [TransferFusdResultCode.ParseSignErr]:
    "toast.transferFusd.result.parseSignErr",
  [TransferFusdResultCode.SignerErr]: "toast.transferFusd.result.signerErr",
  [TransferFusdResultCode.ToNotExistErr]:
    "toast.transferFusd.result.toNotExistErr",
  [TransferFusdResultCode.ToSelfErr]: "toast.transferFusd.result.toSelfErr",
  [TransferFusdResultCode.Exception]: "toast.transferFusd.result.exception",
  [TransferFusdResultCode.Busy]: "toast.transferFusd.result.busy",
  [TransferFusdResultCode.UmbrellaErr]: "toast.transferFusd.result.umbrellaErr",
  [TransferFusdResultCode.AccountFreeze]:
    "toast.transferFusd.result.accountFreeze",
};
